import * as React from 'react';
import { Row, Col } from 'react-bootstrap';
import type { ConnectedProps } from 'react-redux';
import { connect } from 'react-redux';
import type { FilterOptionOption } from 'react-select';
import type { FormErrorsWithArray } from 'redux-form';
import { Field, FieldArray } from 'redux-form';

import type AccountOptionVM from 'ab-viewModels/account/option.viewModel';
import type ItemDepartmentVM from 'ab-viewModels/ItemDepartment/itemDepartment.viewModel';

import { SegmentLabel } from '@acceligentllc/storybook';

import Select from 'af-fields/SelectField';
import Textarea from 'af-fields/Textarea';

import * as AccountActions from 'af-actions/accounts';
import * as ItemActions from 'af-actions/item';

import type FormModel from './formModel';
import type { AccountFM } from './formModel';
import Order from './Order';
import styles from './styles.module.scss';

interface OwnProps {
	initialized: boolean;
	disabled: boolean;

	errors: FormErrorsWithArray<FormModel, string>;
	setHasUnsavedItems: (value: boolean) => void;
}

type Props = OwnProps & ConnectedProps<typeof connector>;

const formatEmployeeOptionLabel = (option: AccountFM) => <div>{option.fullName} ({option.uniqueId})</div>;

const getSelectOptionValue = (option: { id: number; }) => option.id;
const filterSelects = (textA?: string, textB?: string) => (textA ?? '').toLowerCase().includes((textB ?? '').toLowerCase());
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const filterEmployee = ({ data }: FilterOptionOption<any>, text: string) => filterSelects(data.fullName, text);

const accountOptionMapper = (account: AccountOptionVM): AccountFM => {
	return {
		id: account.id,
		fullName: account.fullName,
		uniqueId: account.user.uniqueId,
	};
};

const AccountSelect = Select as unknown as new () => typeof Select<{ id: number; serialNumber: string; toolType: string; }>;

const InternalRepairSection = (props: Props) => {
	const {
		findAllItems,
		findAllTechnicians,
		initialized,
		disabled,
		errors,
		setHasUnsavedItems,
	} = props;
	const [loading, setLoading] = React.useState(false);
	const [items, setItems] = React.useState<ItemDepartmentVM[]>([]);
	const [technicians, setTechnicians] = React.useState<AccountFM[]>([]);

	React.useEffect(() => {
		setLoading(true);
	}, []);

	React.useEffect(() => {
		if (loading) {
			const loadStuff = async () => {
				setItems(await findAllItems());

				const _technicians = await findAllTechnicians();
				setTechnicians((_technicians ?? []).map(accountOptionMapper));

				setLoading(false);
			};
			loadStuff();
		}
	}, [loading, findAllItems, findAllTechnicians]);

	return (
		<div className={styles.section}>
			<SegmentLabel
				label="Internal Repair"
				tooltip="Details of the internally conducted repair, including the assigned technician, internal comments, and a itemized list of all inventory items used during the repair process."
			/>
			<Row className="m-t-m">
				<Col md={6}>
					<Field
						component={AccountSelect}
						containerId="accqa__Select_Technician_Internal"
						filterOption={filterEmployee}
						formatOptionLabel={formatEmployeeOptionLabel}
						getOptionValue={getSelectOptionValue}
						isDisabled={disabled}
						label="Technician name"
						name="repairedBy"
						options={technicians}
						placeholder="Select Technician"
					/>
					<Field
						component={Textarea}
						controlCursor={true}
						disabled={disabled}
						label="Internal Comment"
						name="internalComment"
						rows={6}
					/>
				</Col>
				<Col md={18}>
					<FieldArray
						availableItems={items}
						component={Order}
						disabled={disabled}
						errors={errors}
						initialized={initialized}
						isReadOnly={false}
						name="items"
						setHasUnsavedItems={setHasUnsavedItems}
					/>
				</Col>
			</Row>
		</div>
	);
};

function mapDispatchToProps() {
	return {
		findAllItems: ItemActions.findItemDepartments,
		findAllTechnicians: AccountActions.findAllAssignableAsTechnician,
	};
}

const connector = connect(null, mapDispatchToProps());

export default connector(InternalRepairSection);
